import { useCallback } from 'react';

import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useDeepCompareEffect } from 'react-use';

import { closeBanner, openBanner } from '@/redux/banners/actions';
import { getPermissions } from '@/redux/permissions/selectors';
import { selectAccountSlug } from '@/redux/session/selectors';
import { GlobalBannerAction, GlobalBannerDescriptor } from '@/shared/types/banners';
import { actions } from '@/shared/types/permissions';
import { RootState } from '@/shared/types/state';
import { CONTACT_SALES_URL } from '@/util/constants';

import { useBillingState } from './useBillingState';

interface BannerConfig {
  message: string;
  values?: Record<string, string | number>;
  linkText: string;
  salesUrl?: boolean;
}

export const useBillingBanner = () => {
  const { t } = useTranslation();
  const accountSlug = useSelector(selectAccountSlug);
  const dispatch = useDispatch();

  const canReadBilling = useSelector((state: RootState) =>
    getPermissions(state, 'billing', actions.READ)
  );
  const billingState = useBillingState();

  /**
   * Builds the banner's call to action, which points at the billing page (or at sales for custom
   * plans) and degrades to plain text when the user cannot read billing.
   * @param linkText - Translation key for the action label
   * @param salesUrl - Whether the action points at the sales contact URL instead of the billing page
   */
  const createAction = useCallback(
    ({
      linkText,
      salesUrl = false,
    }: Omit<BannerConfig, 'message' | 'values'>): GlobalBannerAction => {
      if (!canReadBilling) {
        return { kind: 'text', label: t('permissions.contact_admin') };
      }

      return {
        kind: 'link',
        label: t(linkText),
        to: salesUrl ? CONTACT_SALES_URL : `/${accountSlug}/account/billing`,
        external: salesUrl,
      };
    },
    [canReadBilling, accountSlug, t]
  );

  const createBanner = useCallback(
    ({ message, values, linkText, salesUrl }: BannerConfig): GlobalBannerDescriptor => ({
      message: t(message, values),
      action: createAction({ linkText, salesUrl }),
    }),
    [createAction, t]
  );

  useDeepCompareEffect(() => {
    if (billingState === undefined) {
      return undefined;
    }

    let banner: GlobalBannerDescriptor | undefined;
    const { days_left, plan_name } = billingState;

    switch (billingState?.plan_type) {
      case 'trial':
        if (billingState.has_expired) {
          banner = createBanner({
            message: 'billing.your_trial_expired',
            linkText: 'billing.update_your_plan',
          });
        } else if (billingState.is_expiring) {
          banner = createBanner({
            message: 'billing.days_left',
            values: { 0: days_left },
            linkText: 'billing.update_your_plan',
          });
        }
        break;

      case 'custom':
        if (billingState.has_expired) {
          banner = createBanner({
            message: 'billing.your_plan_expired',
            linkText: 'billing.contact_sales',
            salesUrl: true,
          });
        } else if (billingState.is_expiring) {
          banner = createBanner({
            message: 'billing.days_plan_left',
            values: { 0: days_left },
            linkText: 'billing.contact_sales',
            salesUrl: true,
          });
        }
        break;

      case 'stripe': {
        let linkText = 'billing.update_your_plan';
        if (!billingState.has_card) {
          linkText = 'billing.add_card_to_continue';
        } else if (billingState.is_canceled) {
          linkText = 'billing.resume_plan';
        }

        if (billingState.has_expired) {
          banner = createBanner({
            message: 'billing.account_expired',
            values: { 0: plan_name },
            linkText,
          });
        } else if (billingState.is_expiring) {
          banner = createBanner({
            message: 'billing.account_days_left',
            values: { 0: days_left, 1: plan_name },
            linkText,
          });
        }
        break;
      }
    }

    if (!banner) {
      return undefined;
    }
    dispatch(openBanner(banner));

    return () => {
      dispatch(closeBanner());
    };
  }, [dispatch, billingState, createBanner]);
};
